import { useMemo, useRef, useState } from "react";

import { Icon } from "~/components/icon";
import type { Cluster } from "~/types";
import { cn } from "~/utils";
import type { Wave } from "~/utils/fleet";

export type { Wave };

/// Drag-and-drop editor for fleet operation waves. Drag state is local so
/// drag events re-render only this small subtree, keeping drags snappy.
export function FleetWaveEditor({
  clusters,
  waves,
  onChange,
}: {
  clusters: Cluster[];
  waves: Wave[];
  onChange: (waves: Wave[]) => void;
}) {
  const [draggingClusterId, setDraggingClusterId] = useState<string | null>(
    null,
  );
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const lockedClusterIds = useMemo(
    () =>
      new Set(clusters.filter((c) => c.lock_reason != null).map((c) => c.id)),
    [clusters],
  );
  // Whether a native drag is in flight. The dragging state is armed on a
  // deferred timer (see onDragStart), which on a fast drag can fire after the
  // drop already landed — this ref lets that late timer no-op.
  const dragLive = useRef(false);

  const endDrag = () => {
    dragLive.current = false;
    setDropTarget(null);
    setDraggingClusterId(null);
  };

  const withoutCluster = (prev: Wave[], clusterId: string) =>
    prev.map((wave) => ({
      ...wave,
      clusters: wave.clusters.filter((id) => id !== clusterId),
    }));

  const finishDrop = (move: (waves: Wave[]) => Wave[]) => {
    onChange(move(waves).filter((wave) => wave.clusters.length > 0));
    endDrag();
  };

  const zoneHandlers = (
    key: string,
    onDropped: (clusterId: string) => void,
  ) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      if (dropTarget !== key) setDropTarget(key);
    },
    onDragLeave: () => setDropTarget((t) => (t === key ? null : t)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      // The dataTransfer payload is the source of truth; the React drag state
      // only drives styling and may lag behind the native drag.
      const clusterId =
        e.dataTransfer.getData("text/plain") || draggingClusterId;
      if (clusterId) onDropped(clusterId);
    },
  });

  const dragging = draggingClusterId != null;
  const removeCluster = (clusterId: string) => {
    onChange(
      withoutCluster(waves, clusterId).filter(
        (wave) => wave.clusters.length > 0,
      ),
    );
  };

  return (
    <div className="text-xs">
      {waves.map((wave, waveIdx) => (
        <div key={waveIdx}>
          <WaveGap
            visible={dragging}
            active={dropTarget === `gap-${waveIdx}`}
            {...zoneHandlers(`gap-${waveIdx}`, (clusterId) =>
              finishDrop((prev) => {
                const next = withoutCluster(prev, clusterId);
                next.splice(waveIdx, 0, { clusters: [clusterId] });
                return next;
              }),
            )}
          />
          <div
            {...zoneHandlers(`wave-${waveIdx}`, (clusterId) =>
              finishDrop((prev) => {
                const next = withoutCluster(prev, clusterId);
                next[waveIdx] = {
                  ...next[waveIdx],
                  clusters: [...next[waveIdx].clusters, clusterId],
                };
                return next;
              }),
            )}
            className={cn(
              "grid grid-cols-[9.5rem_1fr] gap-x-2 rounded px-1 py-1",
              dropTarget === `wave-${waveIdx}` &&
                "bg-blue-50 ring-1 ring-blue-300",
            )}
          >
            <span className="truncate pt-0.5 text-gray-400" title={wave.label}>
              {wave.label ?? `wave ${waveIdx + 1}`}
            </span>
            <div className="space-y-0.5">
              {wave.clusters.map((clusterId) => {
                const locked = lockedClusterIds.has(clusterId);
                return (
                  <div
                    key={clusterId}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", clusterId);
                      e.dataTransfer.effectAllowed = "move";
                      // Deferred: a synchronous re-render here makes Chrome
                      // janky or cancels the drag outright. setTimeout, not
                      // rAF — Chrome can suppress rAF during a native drag.
                      dragLive.current = true;
                      setTimeout(() => {
                        if (dragLive.current) setDraggingClusterId(clusterId);
                      }, 0);
                    }}
                    onDragEnd={endDrag}
                    className={cn(
                      "flex cursor-grab items-center gap-1.5 active:cursor-grabbing",
                      draggingClusterId === clusterId && "opacity-40",
                    )}
                  >
                    <Icon
                      name="GripVertical"
                      size={12}
                      className="shrink-0 text-gray-300"
                    />
                    <span className="truncate font-mono text-gray-700">
                      {clusterId}
                    </span>
                    {locked ? (
                      <span className="shrink-0 text-orange-600">(locked)</span>
                    ) : null}
                    <button
                      type="button"
                      draggable={false}
                      className="ml-auto shrink-0 rounded p-0.5 text-gray-300 hover:bg-gray-100 hover:text-gray-600"
                      aria-label={`Remove ${clusterId} from fleet operation`}
                      title="Remove from fleet operation"
                      onClick={() => removeCluster(clusterId)}
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
      <WaveGap
        visible={dragging}
        active={dropTarget === `gap-${waves.length}`}
        {...zoneHandlers(`gap-${waves.length}`, (clusterId) =>
          finishDrop((prev) => {
            const next = withoutCluster(prev, clusterId);
            next.push({ clusters: [clusterId] });
            return next;
          }),
        )}
      />
      {dragging ? null : (
        <div className="mt-1.5 px-1 text-[10px] text-gray-400">
          Drag clusters between waves; drop on a gap to start a new wave, or
          remove them from the operation.
        </div>
      )}
    </div>
  );
}

/// Drop zone between waves; visible only while a cluster is being dragged.
/// Dropping here starts a new wave at this position.
function WaveGap({
  visible,
  active,
  ...dragHandlers
}: {
  visible: boolean;
  active: boolean;
  onDragOver: React.DragEventHandler;
  onDragLeave: React.DragEventHandler;
  onDrop: React.DragEventHandler;
}) {
  if (!visible) return <div className="h-1" />;
  return (
    <div
      {...dragHandlers}
      className={cn(
        "my-0.5 flex h-5 items-center justify-center rounded border border-dashed text-[10px]",
        active
          ? "border-blue-400 bg-blue-50 text-blue-600"
          : "border-gray-300 text-gray-400",
      )}
    >
      new wave
    </div>
  );
}
